/* One owner for gift writes; checkout waits for verified eligibility and cart state. */
(function (root) {
  'use strict';

  // A gift is its whole product (every variant) when the product id is known,
  // matching the checkout rule; otherwise its configured variant.
  function isGiftLine(item, gift) {
    return gift.productId != null
      ? String(item.product_id) === String(gift.productId)
      : String(item.variant_id) === String(gift.variantId);
  }

  // Old dedicated promotional SKUs can outlive their offer. A marker alone
  // does not make ordinary merchandise a gift (including a BOGO frame).
  function isLegacyGiftLine(item) {
    const marker = item.properties?._auto_added;
    const automatic = marker === true || marker === 'true' || marker === 1 || marker === '1';
    return /-gift$/i.test(item.handle || '') &&
      (Number(item.original_price ?? item.price) === 0 || automatic);
  }

  // Order protection (assets/order-protection.js) is never qualifying spend or a
  // qualifying item. Falls back to the same vendor/SKU rule before that loads.
  function isProtectionLine(item) {
    const shared = root.JamesOroProtection?.isProtectionLine;
    if (shared) return shared(item);
    return item.vendor === 'Order Protection' || String(item.sku || '').startsWith('JO-PROTECT-');
  }

  // Qualifying spend, in cents — the same rule checkout enforces
  // (apps/jamesoro/extensions/gift-validation in the James Oro app), minus
  // order protection lines:
  // non-gift lines only (gifts never qualify themselves or each other, and a
  // line-item marker such as _auto_added neither makes nor unmakes a gift),
  // after line and cart-level discounts unless the published policy says
  // before_discounts, and gift-card products excluded only when the published
  // policy says so. With no published policy this is the existing theme basis.
  function qualifyingSpend(cart, gifts, policy) {
    const before = policy?.spendBasis === 'before_discounts';
    let spend = 0;
    for (const item of cart.items || []) {
      if (isProtectionLine(item) || isLegacyGiftLine(item) || gifts.some((gift) => isGiftLine(item, gift))) continue;
      if (item.gift_card && policy?.countGiftCardProducts === false) continue;
      spend += before ? item.original_line_price : item.final_line_price;
    }
    if (!before) {
      spend -= (cart.cart_level_discount_applications || []).reduce(
        (sum, discount) => sum + discount.total_allocated_amount, 0
      );
    }
    return Math.max(0, spend);
  }

  function isCountGiftLine(item, reward) {
    return isGiftLine(item, reward) && (Number(item.original_price ?? item.price) === 0 ||
      [true, 'true', 1, '1'].includes(item.properties?._auto_added));
  }

  // The collection lookup is ~1.4 MB of JSON; reuse it across page views for a
  // few minutes so it doesn't sit in front of the first gift add on each page.
  const COLLECTION_IDS_TTL = 10 * 60 * 1000;
  // Gift availability in the current market; short enough to notice a sell-out.
  const GIFT_VARIANT_TTL = 60 * 1000;

  function createCollectionResolver({ config, fetch, cartRoot = '/', windowApi, now = () => Date.now(), storage = null }) {
    const storageKey = `jo-gift-collection:${cartRoot}${config.collectionHandle}`;
    let cachedIds;
    let giftLookup = null;
    function storedIds() {
      try {
        const saved = JSON.parse(storage?.getItem(storageKey) || 'null');
        if (saved && Array.isArray(saved.ids) && now() - saved.at < COLLECTION_IDS_TTL) return new Set(saved.ids);
      } catch (e) {}
      return null;
    }
    async function collectionIds() {
      if (cachedIds) return cachedIds;
      if (!config.collectionHandle) throw new Error('Unable to verify gift eligibility. Please try again.');
      const saved = storedIds();
      if (saved) return (cachedIds = saved);
      const ids = new Set();
      for (let page = 1; page <= 40; page++) {
        const response = await fetch(`${cartRoot}collections/${encodeURIComponent(config.collectionHandle)}/products.json?limit=250&page=${page}`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to verify gift eligibility. Please try again.');
        const data = await response.json();
        if (!Array.isArray(data.products)) throw new Error('Unable to verify gift eligibility. Please try again.');
        data.products.forEach(product => ids.add(String(product.id)));
        if (data.products.length < 250) {
          cachedIds = ids;
          try { storage?.setItem(storageKey, JSON.stringify({ at: now(), ids: [...ids] })); } catch (e) {}
          return ids;
        }
      }
      throw new Error('Unable to verify gift eligibility. Please try again.');
    }
    // Resolves to the configured variant in the current market, or null when the
    // gift product is not sold here (404) or the variant is missing.
    function giftVariant() {
      if (giftLookup && now() - giftLookup.at < GIFT_VARIANT_TTL) return giftLookup.promise;
      const promise = (async () => {
        const response = await fetch(`${cartRoot}products/${encodeURIComponent(config.handle)}.js`, { cache: 'no-store' });
        if (response.status === 404) return null;
        if (!response.ok) throw new Error('Unable to verify gift availability. Please try again.');
        const product = await response.json();
        return product.variants?.find(v => String(v.id) === String(config.variantId)) || null;
      })();
      giftLookup = { at: now(), promise };
      promise.catch(() => { if (giftLookup?.promise === promise) giftLookup = null; });
      return promise;
    }
    return async cart => {
      const minimum = Math.max(1, Number(config.minimum) || 1);
      const window = windowApi.windowState(config.window, now());
      const state = { ...config, minimum, count: 0, available: false, active: window.active, status: 'unavailable', cartKey: JSON.stringify(cart.items.map(item => [item.key, item.quantity, item.final_line_price])) };
      if (!window.valid) return { ...state, status: 'invalid' };
      if (!window.active) return { ...state, status: now() < window.start ? 'upcoming' : 'ended' };
      // Both lookups are independent; run them together.
      const [ids, variant] = await Promise.all([collectionIds(), config.handle ? giftVariant() : null]);
      state.count = (cart.items || []).reduce((count, item) => count + (
        ids.has(String(item.product_id)) && !isGiftLine(item, config) && !isLegacyGiftLine(item) && !item.gift_card && !isProtectionLine(item)
          ? item.quantity : 0), 0);
      if (!config.handle || !variant) return state;
      state.available = Boolean(variant.available && Number(variant.price) === 0);
      const currentWindow = windowApi.windowState(config.window, now());
      state.active = currentWindow.active;
      state.status = !currentWindow.valid ? 'invalid' : !state.active ? 'ended' : !state.available ? 'unavailable' : state.count >= minimum ? 'eligible' : 'progress';
      return state;
    };
  }

  function plan(cart, gifts, policy, { collectionGift = null } = {}) {
    const spend = qualifyingSpend(cart, gifts, policy);
    const updates = {};
    const additions = [];
    // Both gift modes use this planner; obsolete promotional SKUs are removed.
    for (const line of cart.items) {
      if (isLegacyGiftLine(line) && !gifts.some((gift) => isGiftLine(line, gift)) &&
          !(collectionGift && isCountGiftLine(line, collectionGift))) {
        updates[line.key] = 0;
      }
    }
    const uniqueGifts = new Map();
    for (const gift of gifts) {
      const id = String(gift.variantId);
      const previous = uniqueGifts.get(id);
      if (!previous || (gift.available && (!previous.available || gift.threshold < previous.threshold))) uniqueGifts.set(id, gift);
    }
    for (const gift of uniqueGifts.values()) {
      const lines = cart.items.filter((item) => isGiftLine(item, gift));
      let remaining = gift.available && spend >= gift.threshold ? 1 : 0;
      for (const line of lines) {
        const quantity = Math.min(line.quantity, remaining);
        remaining -= quantity;
        if (quantity !== line.quantity) updates[line.key] = quantity;
      }
      if (remaining) additions.push({ id: gift.variantId, quantity: 1, properties: { _auto_added: true } });
    }
    if (collectionGift) {
      let remaining = collectionGift.active && collectionGift.available && collectionGift.count >= collectionGift.minimum ? 1 : 0;
      for (const line of cart.items.filter(item => isCountGiftLine(item, collectionGift))) {
        const quantity = Math.min(line.quantity, remaining);
        remaining -= quantity;
        if (quantity !== line.quantity) updates[line.key] = quantity;
      }
      if (remaining) additions.push({ id: collectionGift.variantId, quantity: 1, properties: { _auto_added: true } });
    }
    return { updates, additions };
  }

  function createController({ readCart, updateCart, addItems, gifts, policy = null, resolveCollectionGift = null, onChange = () => {}, onSettled = () => {} }) {
    const pending = new Set();
    let revision = 0;
    let running = null;
    function track(promise) {
      revision++;
      const settled = Promise.resolve(promise).then(() => {}, () => {});
      pending.add(settled);
      settled.then(() => { pending.delete(settled); revision++; });
      return promise;
    }
    async function settle() {
      while (pending.size) await Promise.all([...pending]);
    }
    async function reconcile() {
      let changed = false;
      for (let attempt = 0; attempt < 12; attempt++) {
        await settle();
        const started = revision;
        const cart = await readCart();
        if (pending.size || started !== revision) continue;
        const collectionGift = resolveCollectionGift ? await resolveCollectionGift(cart) : null;
        if (pending.size || started !== revision) continue;
        const next = plan(cart, gifts, policy, { collectionGift });
        if (Object.keys(next.updates).length) {
          await updateCart(next.updates);
          changed = true;
        } else if (next.additions.length) {
          await addItems(next.additions);
          changed = true;
        } else {
          if (changed) await onChange(cart, collectionGift);
          else await onSettled(cart, collectionGift);
          return cart;
        }
        // Re-read after every write: never trust a stale line index or a local
        // prediction of totals, quantities, or concurrently changed merchandise.
      }
      throw new Error('Cart is still changing. Please try checkout again.');
    }
    function sync() {
      if (!running) running = reconcile().finally(() => { running = null; });
      return running;
    }
    async function checkout() {
      // A background pass may have started before this click. Finish it, then
      // always run a fresh pass for this checkout intent.
      // A failed background pass must not fail this click; the fresh pass decides.
      if (running) await running.catch(() => {});
      return sync();
    }
    return { track, sync, checkout };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { plan, qualifyingSpend, createController, createCollectionResolver };
  const config = root.JamesOroGiftConfig;
  if (!config?.enabled || root.JamesOroGiftGuard) return;
  // The progress bar renders with the same spend rule.
  root.JamesOroGiftSpend = qualifyingSpend;
  const nativeFetch = root.fetch.bind(root);
  const cartRoot = root.Shopify?.routes?.root || '/';
  // Private windows and blocked site data can make sessionStorage throw.
  function sessionStore() {
    try { return root.sessionStorage || null; } catch (e) { return null; }
  }
  async function request(path, body) {
    const response = await nativeFetch(cartRoot + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    } : { cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to update your cart. Please try again.');
    return response.json();
  }
  function giftSectionTargets() {
    const cartItems = document.querySelector('cart-items');
    const targets = [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
    if (cartItems && document.getElementById('main-cart-items')) targets.push(...cartItems.getSectionsToRender());
    return targets;
  }
  function sectionIds(targets) {
    return [...new Set(targets.map((target) => target.section))];
  }
  // Sections rendered by the gift add itself. Valid only while that add is the
  // last cart write; any later write (ours or the theme's) discards them.
  let addedSections = null;
  async function addGiftItems(items) {
    addedSections = null;
    const sections = config.cleanupOnly ? sectionIds(giftSectionTargets()) : [];
    const result = await request('cart/add.js', sections.length
      ? { items, sections: sections.join(','), sections_url: root.location.pathname }
      : { items });
    if (result?.sections && sections.every((id) => result.sections[id])) addedSections = result.sections;
    return result;
  }
  async function refreshGiftCartSections(cart) {
    if (!config.cleanupOnly && typeof root.refreshCartSections === 'function') {
      return root.refreshCartSections();
    }
    const cartItems = document.querySelector('cart-items');
    const drawer = document.querySelector('cart-drawer');
    const targets = giftSectionTargets();
    if (!drawer && !cartItems && !document.getElementById('cart-icon-bubble')) return;
    const sections = sectionIds(targets);
    let html = addedSections && sections.every((id) => addedSections[id]) ? addedSections : null;
    addedSections = null;
    if (!html) {
      const response = await nativeFetch(`${cartRoot}?sections=${sections.join(',')}`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to refresh your cart. Please try again.');
      html = await response.json();
    }
    for (const target of targets) {
      const destination = document.getElementById(target.id);
      if (!destination || !html[target.section]) continue;
      const source = new DOMParser().parseFromString(html[target.section], 'text/html').querySelector(target.selector);
      if (source) (destination.querySelector(target.selector) || destination).innerHTML = source.innerHTML;
    }
    for (const element of [cartItems, drawer, document.getElementById('main-cart-footer')]) {
      element?.classList.toggle('is-empty', cart.item_count === 0);
    }
    drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    if (typeof root.initCartDrawerCountdown === 'function') root.initCartDrawerCountdown();
    if (drawer && typeof root.initCartDrawerUpsellNav === 'function') root.initCartDrawerUpsellNav(drawer);
    if (typeof root.updateBogoCartPrompt === 'function') root.updateBogoCartPrompt(false);
  }
  const controller = createController({
    gifts: config.gifts,
    policy: config.policy || null,
    resolveCollectionGift: config.collectionGift ? createCollectionResolver({ config: config.collectionGift, fetch: nativeFetch, cartRoot, windowApi: root.JamesOroPromotionWindow, storage: sessionStore() }) : null,
    readCart: () => request('cart.js'),
    updateCart: (updates) => { addedSections = null; return request('cart/update.js', { updates }); },
    addItems: addGiftItems,
    // No gift change was needed, but the cart did change (e.g. a quantity
    // edit): keep the progress bar on the verified cart, not one step behind.
    onSettled: (cart, collectionGift) => {
      root.JamesOroCollectionGiftState = collectionGift;
      document.dispatchEvent(new CustomEvent('gift-cart:state', { detail: { cart, collectionGift } }));
      document.dispatchEvent(new CustomEvent('gift-cart:updated', { detail: cart }));
    },
    onChange: async (cart, collectionGift) => {
      root.JamesOroCollectionGiftState = collectionGift;
      document.dispatchEvent(new CustomEvent('gift-cart:updated', { detail: cart }));
      await refreshGiftCartSections(cart);
      document.dispatchEvent(new CustomEvent('gift-cart:state', { detail: { cart, collectionGift } }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-cart-guard' });
      }
    }
  });
  root.JamesOroGiftGuard = controller;
  let scheduled;
  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(() => controller.sync().catch(error => {
      root.JamesOroCollectionGiftState = null;
      document.dispatchEvent(new CustomEvent('gift-cart:error', { detail: error.message }));
    }), 0);
  }
  // Observe theme/app Ajax mutations, including adds and linked-item cleanup.
  // Return the original response/promise unchanged to existing consumers.
  root.fetch = function (input, options) {
    const result = nativeFetch(input, options);
    const url = new URL(input?.url || String(input), root.location.href);
    if (url.origin === root.location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
      addedSections = null;
      controller.track(result);
      Promise.resolve(result).then(schedule, schedule);
    }
    return result;
  };

  const checkoutSelector = '#CartDrawer-Checkout, #checkout, [name="checkout"], a[href="/checkout"], a[href^="/checkout?"]';
  let continuing = false;
  let intent = false;
  // Progress is announced to screen readers only; sighted shoppers see the
  // checkout button's busy state. A visible message appears only on failure.
  function statusRegion() {
    let status = document.getElementById('gift-cart-guard-status');
    if (!status) {
      status = document.createElement('p');
      status.id = 'gift-cart-guard-status';
      status.setAttribute('role', 'status');
      document.body.appendChild(status);
    }
    return status;
  }
  function announce(message, { visible = false } = {}) {
    const status = statusRegion();
    status.style.cssText = visible
      ? 'position:fixed;bottom:16px;left:16px;right:16px;z-index:10000;padding:16px;background:#fff;color:#111;border:1px solid #111'
      : 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';
    status.setAttribute('role', visible ? 'alert' : 'status');
    status.textContent = message;
    status.hidden = !message;
  }
  function setBusy(control, busy) {
    if (!control) return;
    if (busy) {
      control.setAttribute('aria-busy', 'true');
      control.style.opacity = '0.6';
      control.style.cursor = 'progress';
    } else {
      control.removeAttribute('aria-busy');
      control.style.opacity = '';
      control.style.cursor = '';
    }
  }
  async function guard(event) {
    if (continuing) return;
    const trigger = event.type === 'submit'
      ? event.submitter || event.target.querySelector(checkoutSelector)
      : event.target.closest?.(checkoutSelector);
    if (!trigger?.matches(checkoutSelector)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (intent) return;
    intent = true;
    setBusy(trigger, true);
    announce('Updating your cart…');
    try {
      await controller.checkout();
      announce('');
      // A gift update may replace the drawer and its original checkout control.
      // Find the same control again by id, so "Checkout without protection" never
      // resumes as the button that adds it.
      const current = trigger.isConnected ? trigger
        : trigger.id ? document.getElementById(trigger.id) : document.querySelector(checkoutSelector);
      if (!current) throw new Error('Please reopen your cart and try checkout again.');
      continuing = true;
      try {
        if (event.type === 'submit' && current.form) current.form.requestSubmit(current);
        else current.click(); // Preserve the existing BOGO confirmation flow.
      } finally { continuing = false; }
    } catch (error) {
      announce(error.message || 'Unable to verify your gifts. Please try checkout again.', { visible: true });
    } finally {
      intent = false;
      setBusy(trigger, false);
    }
  }
  // Back/forward cache: the browser restores the page exactly as it was left
  // — often mid-redraw, because checkout navigated away while the drawer was
  // still re-rendering. Always redraw from the server on a restore.
  async function redrawAfterRestore() {
    if (/\/cart\/?$/.test(root.location.pathname)) {
      root.location.reload();
      return;
    }
    const cart = await controller.sync();
    document.dispatchEvent(new CustomEvent('gift-cart:updated', { detail: cart }));
    await refreshGiftCartSections(cart);
  }
  if (config.collectionGift?.window?.enabled) {
    const window = root.JamesOroPromotionWindow.windowState(config.collectionGift.window);
    for (const boundary of [window.start, window.end]) {
      const delay = boundary - Date.now();
      if (window.valid && delay > 0 && delay < 2147483647) setTimeout(schedule, delay + 10);
    }
  }
  root.addEventListener('focus', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  document.addEventListener('click', guard, true);
  document.addEventListener('submit', guard, true);
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('cart:refresh', schedule);
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('#cart-icon-bubble')) schedule();
  });
  root.addEventListener('ig:CartUpdated', schedule);
  document.addEventListener('DOMContentLoaded', schedule);
  root.addEventListener('pageshow', (event) => {
    if (event.persisted) redrawAfterRestore().catch(() => {});
    else schedule();
  });
})(typeof window !== 'undefined' ? window : globalThis);
